import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import { formatDateOnly } from '../utils/dateOnly';
import './Changelog.css';

const ChangelogAdmin = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ title: '', description: '', type: 'feature', published_at: '' });
  const [editing, setEditing] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const token = localStorage.getItem('token');

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(getApiUrl('changelog'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : []);
        setItems(list);
      } else {
        setError('Falha ao carregar changelog');
      }
    } catch (err) {
      console.error('Erro ao carregar changelog:', err);
      setError('Erro ao carregar changelog');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setForm({ title: '', description: '', type: 'feature', published_at: '' });
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? getApiUrl(`changelog/${editing.id}`) : getApiUrl('changelog');
      const method = editing ? 'PUT' : 'POST';
      const payload = {
        title: form.title,
        description: form.description,
        type: form.type,
        published_at: form.published_at || undefined
      };
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        await fetchItems();
        resetForm();
      } else {
        const errText = await res.text();
        setError(`Falha ao salvar: ${errText}`);
      }
    } catch (err) {
      console.error('Erro ao salvar changelog:', err);
      setError('Erro ao salvar changelog');
    }
  };

  const startEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title,
      description: item.description,
      type: item.type,
      published_at: item.date
    });
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const res = await fetch(getApiUrl(`changelog/${itemToDelete.id}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        await fetchItems();
        closeDeleteModal();
      } else {
        setError('Falha ao excluir entrada');
      }
    } catch (err) {
      console.error('Erro ao excluir entrada:', err);
      setError('Erro ao excluir entrada');
    } finally {
      closeDeleteModal();
    }
  };

  const remove = (item) => {
    openDeleteModal(item);
  };

  const formatDate = formatDateOnly;

  return (
    <div className="changelog-page">
      <header className="changelog-header">
        <h1>⚙️ Gerenciar Changelog</h1>
        <button className="back-btn" onClick={() => navigate('/changelog')}>← Voltar</button>
      </header>

      <main className="changelog-content">
        {loading && <div className="loading">Carregando...</div>}
        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit} className="admin-card changelog-form">
          <div className="form-field">
            <label htmlFor="title">Título</label>
            <input
              id="title"
              type="text"
              className="input-field"
              placeholder="Digite o título"
              value={form.title}
              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
              required
            />
          </div>

          <div className="form-field">
            <label htmlFor="description">Descrição</label>
            <textarea
              id="description"
              className="input-field"
              placeholder="Descreva a mudança"
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              required
            />
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="type">Tipo</label>
              <select
                id="type"
                className="input-field"
                value={form.type}
                onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="feature">Feature</option>
                <option value="improvement">Improvement</option>
                <option value="bugfix">Bugfix</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="published_at">Data de publicação</label>
              <input
                id="published_at"
                type="date"
                className="input-field"
                value={form.published_at}
                onChange={e => setForm(prev => ({ ...prev, published_at: e.target.value }))}
              />
            </div>
          </div>

          <div className="btn-group form-actions">
            <button type="submit" className="btn btn-primary">{editing ? 'Atualizar' : 'Criar'}</button>
            {editing && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancelar</button>
            )}
          </div>
        </form>

        <div className="changelog-list">
          {items.map((item) => (
            <div key={item.id} className={`changelog-item chg-${item.type}`}>
              <div className="item-icon">{item.icon || '📝'}</div>
              <div className="item-content">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <span className="item-date">{formatDate(item.date)}</span>
                <div className="btn-group action-buttons">
                  <button className="btn btn-secondary btn-sm" onClick={() => startEdit(item)}>Editar</button>
                  <button className="btn btn-danger btn-sm" onClick={() => remove(item)}>Excluir</button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div className="empty">Nenhuma entrada criada ainda.</div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar Exclusão</h2>
              <button className="close-btn" onClick={closeDeleteModal}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#991b1b', margin: 0, fontWeight: '500' }}>
                  ⚠️ Ação irreversível
                </p>
              </div>
              <p>
                Tem certeza que deseja deletar a entrada <strong>"{itemToDelete.title}"</strong>?
              </p>
              <p style={{ color: '#666', fontSize: '0.9em' }}>
                Esta ação não pode ser desfeita.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={closeDeleteModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                🗑️ Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangelogAdmin;